<main>
  <loading-overlay [isLoading]="isLoading() > 0" />

  <!-- ══ Step 1 — which account receives the funds ══════════════════════════════════════════════════
       Nothing is created until Continue, so Cancel here is a true escape hatch: From/To lock the moment
       the transfer is sent. -->
  @if (isSetup()) {
    <header>
      <h1>{{ 'InternalTransfer_New' | translateWF: 'New internal transfer' }}</h1>
    </header>

    <div class="content">
      <form [formGroup]="setupForm" class="max-w-[46rem]">
        <p-panel>
          <ng-template pTemplate="header">
            <div class="col">
              <span>{{ 'InternalTransfer_Setup_Question' | translateWF: 'Which account will receive the funds?' }}</span>
              @if (fromAccount(); as source) {
                <small class="text-surface-500 font-normal">
                  {{ 'InternalTransfer_Setup_Sending' | translateWF: 'Sending from' }}
                  <strong>{{ source.code }} · {{ source.name }}</strong> ({{ source.bankName }} · {{ currencyLabel() }})
                </small>
              }
            </div>
          </ng-template>
          <p-card>
            <div class="col">
              <div class="form-box">
                <label for="toAccountCode">{{ 'InternalTransfer_Field_ToAccount' | translateWF: 'Receiving account' }} <span class="text-red-500">*</span></label>
                <p-select
                  id="toAccountCode"
                  [options]="destinationOptions()"
                  formControlName="toAccountCode"
                  optionLabel="name"
                  optionValue="code"
                  [filter]="true"
                  filterBy="code,name,bankName"
                  appendTo="body"
                  styleClass="w-full"
                  [placeholder]="'InternalTransfer_Setup_Placeholder' | translateWF: 'Select the account receiving the money'"
                  (onChange)="onDestinationChange($event.value)"
                  [ngClass]="{ 'ng-invalid ng-dirty': setupForm.controls.toAccountCode.invalid && setupForm.controls.toAccountCode.touched }"
                >
                  <ng-template let-account pTemplate="item">
                    <div class="col">
                      <span>{{ account.code }} · {{ account.name }}</span>
                      <!-- The currency is on the row because it is now a real choice: picking an account in
                     another money means the bank converts, and that changes what the receipt has to record. -->
                      <small class="text-surface-500">{{ account.bankName }} · {{ currencyCode(account.currency) }}</small>
                    </div>
                  </ng-template>
                </p-select>
                @if (destinationOptions().length === 0) {
                  <small class="text-surface-500">
                    {{ 'InternalTransfer_Setup_NoAccounts' | translateWF: 'No other active account can receive from this one.' }}
                  </small>
                } @else if (converts()) {
                  <!-- Said before Continue, not after: which currency the money lands in decides what the receipt
                 has to record, and it is the one thing about this choice that is not visible from the name. -->
                  <small class="text-amber-600">
                    <i class="pi pi-info-circle mr-1"></i>
                    {{ 'InternalTransfer_Setup_Converts' | translateWF: 'The bank will convert' }}
                    {{ currencyLabel() }} → {{ arrivalCurrencyLabel() }}.
                    {{ 'InternalTransfer_Setup_ConvertsHint' | translateWF: 'You will enter the rate it gave when you confirm the receipt.' }}
                  </small>
                }
              </div>

              <div class="row-end-stretch gap-2">
                <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="text" (click)="abandonSetup()" />
                <p-button
                  [label]="'Continue' | translateWF: 'Continue'"
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  (click)="continueFromSetup()"
                  [disabled]="saving() || setupForm.invalid"
                />
              </div>
            </div>
          </p-card>
        </p-panel>
      </form>
    </div>
  }

  <!-- ══ Step 2 — the transfer itself ══════════════════════════════════════════════════════════════ -->
  @if (!isSetup()) {
    <header>
      <div class="col-sm">
        <div class="row-start-center gap-3 flex-wrap">
          <h1>{{ 'InternalTransfer' | translateWF: 'Internal transfer' }} {{ transfer()?.number || '' }}</h1>
          <p-tag [severity]="statusSeverity()" [value]="statusLabel()" />
        </div>
        <div class="row-sm-start-center flex-wrap text-sm text-surface-500">
          <span>{{ endpointTitle(fromAccount()) }}</span>
          <i class="pi pi-arrow-right text-surface-400"></i>
          <span>{{ endpointTitle(toAccount()) }}</span>
          <span class="text-surface-300">·</span>
          <span>{{ sentAmount() | number: '1.2-2' }} {{ currencyLabel() }}</span>
          @if (incomingConverts() && amountArrived() > 0) {
            <i class="pi pi-angle-right text-surface-400"></i>
            <span>{{ amountArrived() | number: '1.2-2' }} {{ arrivalCurrencyLabel() }}</span>
          }
          @if (isDraft()) {
            <small class="text-surface-500 ml-2">
              {{ 'InternalTransfer_WrongAccounts' | translateWF: 'Wrong accounts?' }}
              <a class="text-primary cursor-pointer" (click)="cancelTransfer()">{{ 'InternalTransfer_CancelStartOver' | translateWF: 'Cancel and start over.' }}</a>
            </small>
          }
        </div>
      </div>
      <div class="buttons">
        <p-button [label]="'Back' | translateWF: 'Back'" variant="text" (click)="back()" />
        <!-- What can be done to the movement on screen, whichever leg that is. Both legs put their actions
             in the same place, because from here they are the same kind of thing: save this bank movement,
             or post it. Only the transfer's own fields save elsewhere — from the Context card, beside what
             they belong to. -->
        @if (isDraft()) {
          @if (activeTab() === 'outgoing') {
            <p-button [label]="'InternalTransfer_SaveLeg' | translateWF: 'Save movement'" icon="pi pi-save" variant="outlined" (click)="saveLeg()" [disabled]="saving()" />
          }
          <p-button [label]="'InternalTransfer_Send' | translateWF: 'Send transfer'" icon="pi pi-send" severity="success" (click)="send()" [disabled]="saving()" />
        }

        @if (isInTransit() && activeTab() === 'incoming' && hasIncomingLeg()) {
          <p-button
            [label]="'InternalTransfer_DiscardReceipt' | translateWF: 'Discard'"
            icon="pi pi-trash"
            severity="secondary"
            variant="text"
            (click)="discardIncomingLeg()"
            [disabled]="saving()"
          />
          <p-button
            [label]="'InternalTransfer_SaveReceipt' | translateWF: 'Save movement'"
            icon="pi pi-save"
            variant="outlined"
            (click)="saveIncomingLeg()"
            [disabled]="saving()"
          />
          @if (incomingIsReturn()) {
            <!-- Destructive and final: a returned transfer is closed, and correcting one means a new
                 transfer. Hence the modal, not just a button. -->
            <p-button
              [label]="'InternalTransfer_Return' | translateWF: 'Mark as returned'"
              icon="pi pi-undo"
              severity="danger"
              (click)="openReturnDialog()"
              [disabled]="saving()"
            />
          } @else {
            <p-button
              [label]="'InternalTransfer_ConfirmReceipt' | translateWF: 'Confirm receipt'"
              icon="pi pi-check"
              severity="success"
              (click)="receive()"
              [disabled]="saving()"
            />
          }
        }
      </div>
    </header>

    <!-- Which two banks this moves money between, with the leg on screen highlighted. Defined once and
         projected into each leg's form so the two can never describe the accounts differently. -->
    <ng-template #endpointsBar let-active="active">
      <div class="endpoints">
        <div class="endpoints__side" [class.endpoints__side--on]="active === 'outgoing'">
          <span class="endpoints__label">{{ 'InternalTransfer_Outgoing' | translateWF: 'Outgoing' }}</span>
          <span class="endpoints__name"><i class="pi pi-arrow-up-right"></i>{{ endpointTitle(fromAccount()) }}</span>
          <span class="endpoints__meta">{{ endpointDetail(fromAccount()) }}</span>
          @if (legNumber('outgoing'); as txn) {
            <span class="endpoints__meta">{{ 'InternalTransfer_BankTxn' | translateWF: 'Bank txn' }} {{ txn }}</span>
          }
        </div>
        <i class="endpoints__arrow pi pi-arrow-right"></i>
        <div class="endpoints__side" [class.endpoints__side--on]="active === 'incoming'">
          <span class="endpoints__label">{{ 'InternalTransfer_Incoming' | translateWF: 'Incoming' }}</span>
          <span class="endpoints__name"><i class="pi pi-arrow-down-left"></i>{{ endpointTitle(toAccount()) }}</span>
          <span class="endpoints__meta">{{ endpointDetail(toAccount()) }}</span>
          @if (legNumber('incoming'); as txn) {
            <span class="endpoints__meta">{{ 'InternalTransfer_BankTxn' | translateWF: 'Bank txn' }} {{ txn }}</span>
          }
        </div>
      </div>
    </ng-template>

    <div class="content col">
      <!-- The two legs. The incoming tab is locked until the outgoing has posted — not because it is
           derived from the outgoing, but because until the money moves there is nothing yet to report. -->
      <p-selectbutton [options]="tabOptions()" [formControl]="tabControl" optionLabel="label" optionValue="value" optionDisabled="disabled" [allowEmpty]="false" />

      <!-- Outgoing leg ──────────────────────────────────────────────────────────────────────────
           A transfer leg IS a bank movement, so it renders through the same form the standalone movement
           screen uses (#5089). What this page adds is what only it knows: the request it answers, and the
           entries it has posted. -->
      @if (activeTab() === 'outgoing') {
        <form [formGroup]="form">
          <app-bank-movement-form
            mode="transfer-send"
            [controls]="sendControls"
            [currencyLabel]="currencyLabel()"
            [editable]="isDraft()"
            [showPreview]="isDraft()"
            [previewLines]="sendPreviewLines()"
            [previewTitle]="'InternalTransfer_Section_SendPreview' | translateWF: 'Send entry'"
            [previewSubtitle]="(form.controls.transferDate.value | date: 'dd/MM/yyyy') || ''"
            [instrumentTypeOptions]="instrumentTypeOptions()"
            [feeTypeOptions]="feeTypeOptions()"
          >
            <div movementContext>
              <ng-container *ngTemplateOutlet="endpointsBar; context: { active: 'outgoing' }" />
            </div>

            <!-- What the transfer knows that the movement does not: the request it answers and why it was
                 made. Beside the entry preview rather than under the money, because it belongs to the
                 document, not to the bank movement — and it saves on its own button for the same reason.

                 It stops being editable the moment the outgoing leg posts. By then the cash request has
                 been answered by money that actually moved, and re-pointing it at a different request would
                 rewrite what a posted entry was for. -->
            <div movementAsideLead>
              <p-panel [header]="'InternalTransfer_Section_Context' | translateWF: 'Context'">
                @if (isDraft()) {
                  <ng-template pTemplate="icons">
                    <p-button
                      [label]="'InternalTransfer_SaveContext' | translateWF: 'Save'"
                      icon="pi pi-save"
                      variant="outlined"
                      size="small"
                      (click)="saveDraft()"
                      [disabled]="saving()"
                    />
                  </ng-template>
                }
                <p-card>
                  <div class="col">
                    <div class="form-box">
                      <label for="cashRequestReference">{{ fieldLabels.cashRequestReference | translateWF: 'Cash request' }}</label>
                      <!-- The option carries the week and the figure, not just the number: what a request asks
                       for is the thing being chosen between, and it is needed BEFORE committing (#5135). -->
                      <p-select
                        id="cashRequestReference"
                        [options]="cashRequestSelectOptions()"
                        formControlName="cashRequestReference"
                        optionLabel="label"
                        optionValue="value"
                        [showClear]="true"
                        [filter]="true"
                        filterBy="label"
                        appendTo="body"
                        styleClass="w-full"
                        [placeholder]="'InternalTransfer_CashRequest_Placeholder' | translateWF: 'None — money moved because the other account needed it'"
                      />
                      <small class="text-surface-500">
                        @if (isDraft()) {
                          {{ 'InternalTransfer_CashRequest_Hint' | translateWF: 'Optional. Most transfers have none.' }}
                        } @else {
                          {{ 'InternalTransfer_CashRequest_Locked' | translateWF: 'Fixed once the transfer was sent.' }}
                        }
                      </small>
                    </div>

                    <!-- What the week asked for, stated where the request was chosen rather than only inside the
                     request itself — this transfer IS the funding, so the figure belongs on the screen where
                     the money is sent. -->
                    @if (selectedCashRequest(); as request) {
                      <div class="col-sm rounded-[10px] border border-[var(--p-surface-200)] bg-[var(--p-surface-50)] px-[1.1rem] py-[0.85rem]">
                        <div class="col-sm">
                          <span class="text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--p-text-muted-color)]">{{
                            'InternalTransfer_ToFund' | translateWF: 'Amount to fund'
                          }}</span>
                          <strong class="text-2xl font-bold leading-tight tabular-nums text-[var(--p-primary-color)]">{{ cashRequestTotalLabel() }}</strong>
                        </div>
                        @if (request.weekStart && request.weekEnd) {
                          <span class="text-sm text-[var(--p-text-muted-color)]">
                            {{ 'CashRequest_Week' | translateWF: 'Week' }} {{ request.weekStart | date: 'dd/MM/yyyy' }} –
                            {{ request.weekEnd | date: 'dd/MM/yyyy' }}
                          </span>
                        }

                        <!-- Sending a different figure is legitimate — a partial funding, a top-up — but it has
                         to be visible rather than inferred by comparing two numbers on separate cards. -->
                        @if (cashRequestShortfall()) {
                          <span class="inline-flex items-start gap-1.5 text-sm font-semibold text-amber-600">
                            <i class="pi pi-exclamation-triangle"></i>
                            {{ 'InternalTransfer_Sending' | translateWF: 'Sending' }} {{ money(sentAmount(), fromAccount()?.currency) }} ·
                            {{
                              isUnderCashRequest()
                                ? ('InternalTransfer_UnderRequest' | translateWF: 'short of the request by')
                                : ('InternalTransfer_OverRequest' | translateWF: 'over the request by')
                            }}
                            {{ cashRequestGapLabel() }}
                          </span>
                        }

                        <!-- A transfer moves the SOURCE account's currency; a cash request is always USD. When
                         they differ, no subtraction between them means anything, so none is offered. -->
                        @if (cashRequestCurrencyMismatch()) {
                          <span class="inline-flex items-start gap-1.5 text-sm text-[var(--p-text-muted-color)]">
                            <i class="pi pi-info-circle"></i>
                            {{
                              'InternalTransfer_CashRequest_Fx'
                                | translateWF: "This transfer moves a different currency than the request is planned in — compare the two at the day's rate."
                            }}
                          </span>
                        }
                      </div>
                    }

                    <div class="form-box">
                      <label for="notes">{{ fieldLabels.notes | translateWF: 'Notes' }}</label>
                      <textarea pTextarea id="notes" formControlName="notes" rows="3" class="w-full"></textarea>
                    </div>
                  </div>
                </p-card>
              </p-panel>
            </div>

            @if (showAccounting()) {
              <app-journal-entry-summary movementAside [references]="journalEntryIds()" [entryLabels]="journalEntryLabels()" [currencyLabel]="currencyLabel()" />
            }
          </app-bank-movement-form>
        </form>
      }

      <!-- Incoming leg ──────────────────────────────────────────────────────────────────────────
           Nothing exists here until someone starts it. A movement conjured when the wire left would sit in
           the destination bank's list claiming a date and a figure nobody had seen yet. -->
      @if (activeTab() === 'incoming') {
        @if (transfer()?.returnReason) {
          <p-message severity="error" styleClass="w-full" [text]="('InternalTransfer_Returned' | translateWF: 'Returned') + ': ' + transfer()?.returnReason" />
        }

        @if (!hasIncomingLeg()) {
          <!-- The wire is still in flight. Two ways it can end, and which one it is decides which account
               the money lands in — so it is asked here rather than guessed. -->
          <p-panel>
            <p-card>
              <div class="col-start-center text-center">
                <i class="pi pi-send text-3xl text-primary"></i>
                <h2 class="text-lg font-semibold">{{ 'InternalTransfer_Await_Title' | translateWF: 'Money is in transit' }}</h2>
                <p class="max-w-[34rem] text-surface-500">
                  {{ sentAmount() | number: '1.2-2' }} {{ currencyLabel() }}
                  {{
                    'InternalTransfer_Await_Body'
                      | translateWF: 'left the source account and is held in Cash in Transit. Record what happens next when the bank statement shows it.'
                  }}
                </p>

                @if (isInTransit()) {
                  <div class="flex flex-wrap justify-center gap-2">
                    <p-button
                      [label]="'InternalTransfer_Start_Receipt' | translateWF: 'Record the receipt'"
                      icon="pi pi-download"
                      (click)="startIncomingLeg(false)"
                      [disabled]="saving()"
                    />
                    <p-button
                      [label]="'InternalTransfer_Start_Return' | translateWF: 'It came back — record a return'"
                      icon="pi pi-undo"
                      severity="danger"
                      variant="outlined"
                      (click)="startIncomingLeg(true)"
                      [disabled]="saving()"
                    />
                  </div>
                  <small class="text-surface-500">
                    {{ 'InternalTransfer_Await_Hint' | translateWF: 'Either one creates a draft bank movement you can edit before it posts.' }}
                  </small>
                }
              </div>
            </p-card>
          </p-panel>
        } @else {
          <form [formGroup]="settlementForm" class="col">
            @if (incomingIsReturn() && !isClosed()) {
              <p-message
                severity="warn"
                styleClass="w-full"
                [text]="'InternalTransfer_Recording_Return' | translateWF: 'Recording a RETURN — the money goes back into the source account.'"
              />
            }

            <app-bank-movement-form
              mode="transfer-receive"
              [controls]="settlementControls"
              [currencyLabel]="arrivalCurrencyLabel()"
              [editable]="isInTransit()"
              [showPreview]="isInTransit()"
              [previewLines]="settlementPreviewLines()"
              [previewMixedCurrency]="incomingConverts()"
              [previewTitle]="'InternalTransfer_Section_ReceivePreview' | translateWF: 'Receive entry'"
              [previewSubtitle]="(settlementForm.controls.receivedOn.value | date: 'dd/MM/yyyy') || ''"
              [feeTypeOptions]="feeTypeOptions()"
            >
              <div movementContext>
                <ng-container *ngTemplateOutlet="endpointsBar; context: { active: 'incoming' }" />
              </div>

              <!-- Why the amount above is read-only. The gross is whatever was sent, so the only thing this
                   step establishes is what the banks took on the way — and what landed follows from it. -->
              @if (incomingConverts()) {
                <!-- The one thing a converted receipt knows that its bank movement cannot derive. The amount
                     above is not typed either way — it is what was sent, priced at this rate — so entering
                     the rate is what makes the rest of the form mean anything. -->
                <div movementGauge class="col">
                  <div class="form-box">
                    <label for="exchangeRateToUsd">
                      {{ 'InternalTransfer_Field_ExchangeRate' | translateWF: 'Rate the bank gave' }}
                      <span class="text-red-500">*</span>
                    </label>
                    <p-inputGroup>
                      <p-inputNumber
                        inputId="exchangeRateToUsd"
                        formControlName="exchangeRateToUsd"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="rateDecimals"
                        [min]="0"
                        styleClass="w-full"
                        [placeholder]="'InternalTransfer_Rate_Placeholder' | translateWF: 'e.g. 24.6500'"
                        [ngClass]="{
                          'ng-invalid ng-dirty': settlementForm.controls.exchangeRateToUsd.invalid && settlementForm.controls.exchangeRateToUsd.touched,
                        }"
                      />
                      <p-inputGroupAddon>{{ quotedCurrencyLabel() }} / USD</p-inputGroupAddon>
                    </p-inputGroup>
                    <small class="text-surface-500">
                      {{ 'InternalTransfer_Rate_Hint' | translateWF: "From the wire confirmation — the rate the bank actually gave, not the day's published rate." }}
                    </small>
                  </div>

                  <!-- Stated, not left to be inferred from the amount field: this is the figure the
                       destination bank's statement will show, and it is what the rate is checked against. -->
                  <div class="flex items-baseline justify-between border-t border-[var(--p-surface-200)] pt-3">
                    <span class="text-sm text-[var(--p-text-muted-color)]">
                      {{ 'InternalTransfer_Arrives' | translateWF: 'Arrives in the account' }}
                    </span>
                    <strong class="text-lg tabular-nums">
                      @if (amountArrived() > 0) {
                        {{ amountArrived() | number: '1.2-2' }} {{ arrivalCurrencyLabel() }}
                      } @else {
                        —
                      }
                    </strong>
                  </div>
                  <small class="text-surface-500">
                    {{ sentAmount() | number: '1.2-2' }} {{ currencyLabel() }}
                    {{ 'InternalTransfer_ConvertedAt' | translateWF: 'converted at this rate. Record the charges the receiving bank took below.' }}
                  </small>
                </div>
              } @else {
                <div movementGauge class="locked">
                  <i class="pi pi-lock locked__icon"></i>
                  <span>
                    {{
                      'InternalTransfer_LockedGross_Hint'
                        | translateWF: 'The amount is fixed at what was sent, so every cent is accounted for. Record the charges the banks took; what landed is the rest.'
                    }}
                  </span>
                </div>
              }

              <div movementExtras>
                <p-panel [header]="'InternalTransfer_Section_Attachments' | translateWF: 'Attachments'">
                  <p-card>
                    <label class="dropzone">
                      <input #fileInput type="file" class="hidden" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx" (change)="onAttachment($event, fileInput)" />
                      <i class="pi pi-cloud-upload"></i>
                      <span>{{ 'InternalTransfer_Upload' | translateWF: 'Upload the wire confirmation' }}</span>
                    </label>
                    @if (transfer()?.attachments?.length) {
                      <ul class="attachment-list">
                        @for (att of transfer()!.attachments!; track att.name) {
                          <li>
                            <button type="button" class="attachment-link" (click)="openAttachment(att.presignedUrl)">
                              <i class="pi pi-file"></i><span>{{ fileName(att.name) }}</span>
                            </button>
                          </li>
                        }
                      </ul>
                    }
                  </p-card>
                </p-panel>
              </div>

              @if (showAccounting()) {
                <app-journal-entry-summary movementAside [references]="journalEntryIds()" [entryLabels]="journalEntryLabels()" [currencyLabel]="currencyLabel()" />
              }
            </app-bank-movement-form>
          </form>
        }
      }
    </div>
  }

  <!-- ══ Return confirmation ═══════════════════════════════════════════════════════════════════════
       A return is not a reversal: the send charge was really charged and stays on the books. -->
  <p-dialog
    [visible]="returnDialogVisible()"
    (visibleChange)="$event ? null : closeReturnDialog()"
    [modal]="true"
    [style]="{ width: '32rem' }"
    [header]="'InternalTransfer_Return_Title' | translateWF: 'Mark this transfer as returned?'"
  >
    <div class="col">
      <p-message
        severity="error"
        styleClass="w-full"
        [text]="
          'InternalTransfer_Return_Warning' | translateWF: 'This cannot be undone. A returned transfer is closed — correcting a mistaken return means creating a new transfer.'
        "
      />

      <p class="text-sm text-surface-600">
        {{ 'InternalTransfer_Return_Explain' | translateWF: 'The money goes back into' }}
        <strong>{{ fromAccount()?.code }} · {{ fromAccount()?.name }}</strong
        >.
        {{
          'InternalTransfer_Return_Explain2'
            | translateWF: 'The charge paid to send is not refunded and stays as an expense. Record any return charge on the Incoming leg tab first — what comes back is the rest.'
        }}
      </p>

      <div class="row-between-stretch text-sm">
        <span class="text-surface-500">{{ 'InternalTransfer_TotalLanded' | translateWF: 'Lands in the account' }}</span>
        <strong>{{ amountLanded() | number: '1.2-2' }} {{ arrivalCurrencyLabel() }}</strong>
      </div>

      <form [formGroup]="settlementForm">
        <div class="form-box">
          <label for="reason">{{ settlementFieldLabels.reason | translateWF: 'Reason' }} <span class="text-red-500">*</span></label>
          <textarea
            pTextarea
            id="reason"
            formControlName="reason"
            rows="3"
            class="w-full"
            [placeholder]="'InternalTransfer_Return_ReasonPlaceholder' | translateWF: 'Wrong account, closed account, compliance hold, correspondent rejection…'"
            [ngClass]="{ 'ng-invalid ng-dirty': settlementForm.controls.reason.invalid && settlementForm.controls.reason.touched }"
          ></textarea>
        </div>
      </form>
    </div>

    <ng-template pTemplate="footer">
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="text" (click)="closeReturnDialog()" />
      <p-button
        [label]="'InternalTransfer_Return_Confirm' | translateWF: 'Yes, mark as returned'"
        icon="pi pi-undo"
        severity="danger"
        (click)="confirmReturn()"
        [disabled]="saving() || !settlementForm.controls.reason.value"
      />
    </ng-template>
  </p-dialog>
</main>
